// src/pages/Calculators.js
import React from 'react';
import { useNavigate } from 'react-router-dom';
import './Calculators.css';

/* ─── SVG Icons ─── */
const IconRebar = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="9" width="20" height="6" rx="1" opacity=".15" fill="currentColor"/>
    <line x1="2" y1="12" x2="22" y2="12"/>
    <line x1="6" y1="7" x2="6" y2="17"/>
    <line x1="10" y1="7" x2="10" y2="17"/>
    <line x1="14" y1="7" x2="14" y2="17"/>
    <line x1="18" y1="7" x2="18" y2="17"/>
  </svg>
);
const IconCarbon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 3C7 3 3 7 3 12s4 9 9 9 9-4 9-9" opacity=".15" fill="currentColor"/>
    <path d="M12 3C7 3 3 7 3 12s4 9 9 9 9-4 9-9"/>
    <path d="M15 9a3 3 0 1 1-6 0 3 3 0 0 1 6 0z"/>
    <path d="M18 4l3 3-3 3"/>
  </svg>
);
const IconMesh = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="3" width="18" height="18" rx="1" opacity=".1" fill="currentColor"/>
    <line x1="3" y1="9" x2="21" y2="9"/>
    <line x1="3" y1="15" x2="21" y2="15"/>
    <line x1="9" y1="3" x2="9" y2="21"/>
    <line x1="15" y1="3" x2="15" y2="21"/>
  </svg>
);
const IconWall = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="4" width="20" height="16" rx="1" opacity=".1" fill="currentColor"/>
    <rect x="2" y="4" width="20" height="16" rx="1"/>
    <line x1="2" y1="10" x2="22" y2="10"/>
    <line x1="2" y1="16" x2="22" y2="16"/>
    <line x1="8" y1="4" x2="8" y2="10"/>
    <line x1="14" y1="10" x2="14" y2="16"/>
    <line x1="8" y1="16" x2="8" y2="20"/>
  </svg>
);
const IconColumn = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <rect x="7" y="2" width="10" height="20" rx="1" opacity=".12" fill="currentColor"/>
    <rect x="7" y="2" width="10" height="20" rx="1"/>
    <line x1="7" y1="7" x2="17" y2="7"/>
    <line x1="7" y1="12" x2="17" y2="12"/>
    <line x1="7" y1="17" x2="17" y2="17"/>
  </svg>
);
const IconClock = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" opacity=".12" fill="currentColor"/>
    <circle cx="12" cy="12" r="10"/>
    <path d="M12 7v5l3 3"/>
  </svg>
);
const IconArrow = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M6 4l4 4-4 4"/>
  </svg>
);

/* ─── Calculator definitions ─── */
const CALCULATORS = [
  {
    id: 'carbon',
    label: 'Carbon Calculator',
    desc: 'Estimate embodied carbon of your material composites using EPD carbon factors (kgCO₂e/kg)',
    path: '/user/calculator/carbon',
    Icon: IconCarbon,
    color: 'emerald',
    tags: ['Coming Soon'],
    available: false,
  },
  {
    id: 'mesh',
    label: 'Mesh Estimator',
    desc: 'Quick sheet count and lap-zone calculator for any slab layout with wastage allowance',
    path: '/user/calculator/rebar',
    tabId: 'mesh',
    Icon: IconMesh,
    color: 'sky',
    tags: ['SL Series', 'RL Series', 'Sheet Count', 'Laps'],
    available: true,
  },
  {
    id: 'rebar',
    label: 'Rebar Calculator',
    desc: 'Full steel quantity estimator for floor mesh, beams, walls and columns to AS/NZS 4671 & AS 3600',
    path: '/user/calculator/rebar',
    tabId: 'bars',
    Icon: IconRebar,
    color: 'teal',
    tags: ['Floor Mesh', 'Beams', 'Walls', 'Columns'],
    available: true,
  },
  {
    id: 'wall',
    label: 'Wall Reinforcement',
    desc: 'Vertical, horizontal and starter bar schedules for in-situ concrete and blockwork walls',
    path: '/user/calculator/rebar',
    tabId: 'walls',
    Icon: IconWall,
    color: 'amber',
    tags: ['In-Situ', 'Blockwork', 'Starter Bars'],
    available: true,
  },
  {
    id: 'column',
    label: 'Column & Pier Cages',
    desc: 'Rectangular and circular column cage estimator with end-zone and mid-zone ligature schedules',
    path: '/user/calculator/rebar',
    tabId: 'cols',
    Icon: IconColumn,
    color: 'indigo',
    tags: ['Rectangular', 'Circular', 'Ligatures'],
    available: true,
  },
  {
    id: 'retaining',
    label: 'Retaining Wall',
    desc: 'Retaining wall and tie-back reinforcement estimator — coming in the next release',
    path: null,
    tabId: null,
    Icon: IconClock,
    color: 'violet',
    tags: ['Coming Soon'],
    available: false,
  },
];

/* ─── Component ─── */
const Calculators = () => {
  const navigate = useNavigate();

  const handleCardClick = (calc) => {
    if (calc.available && calc.path) {
      navigate(calc.path, { state: { tab: calc.tabId } });
    }
  };

  const available = CALCULATORS.filter((c) => c.available);
  const coming    = CALCULATORS.filter((c) => !c.available);

  return (
    <div className="calc-hub">

      {/* Header */}
      <header className="calc-hub__header">
        <div className="calc-hub__header-inner">
          <div className="calc-hub__header-text">
            <div className="calc-hub__badge_tools">
              <span className="calc-hub__badge-dot" />
              Engineering Tools
            </div>
            <h1>Calculators</h1>
            <p>Structural quantity estimators built to Australian standards</p>
          </div>
          <div className="calc-hub__header-meta">
            <div className="calc-hub__stat-pill">
              <span className="calc-hub__stat-value">{available.length}</span>
              <span className="calc-hub__stat-label">Active</span>
            </div>
            <div className="calc-hub__stat-pill">
              <span className="calc-hub__stat-value">AS/NZS 4671</span>
              <span className="calc-hub__stat-label">Standard</span>
            </div>
            <div className="calc-hub__stat-pill">
              <span className="calc-hub__stat-value">AS 3600</span>
              <span className="calc-hub__stat-label">Concrete Code</span>
            </div>
          </div>
        </div>
      </header>

      {/* Available calculators */}
      <section className="calc-hub__section">
        <div className="calc-hub__section-header">
          <div className="calc-hub__section-icon">🛠️</div>
          <h2 className="calc-hub__section-title">Available Calculators</h2>
          <span className="calc-hub__section-count">{available.length} tools</span>
        </div>

        <div className="calc-hub__grid">
          {available.map((calc) => (
            <div
              key={calc.id}
              className={`calc-hub__card calc-hub__card--${calc.color}`}
              onClick={() => handleCardClick(calc)}
            >
              <div className={`calc-hub__card-icon calc-hub__card-icon--${calc.color}`}>
                <calc.Icon />
              </div>
              <div className="calc-hub__card-body">
                <h3>{calc.label}</h3>
                <p>{calc.desc}</p>
                <div className="calc-hub__tags">
                  {calc.tags.map((tag) => (
                    <span key={tag} className="calc-hub__tag">{tag}</span>
                  ))}
                </div>
              </div>
              <span className="calc-hub__card-arrow"><IconArrow /></span>
            </div>
          ))}
        </div>
      </section>

      {/* Coming soon */}
      {coming.length > 0 && (
        <section className="calc-hub__section">
          <div className="calc-hub__section-header">
            <div className="calc-hub__section-icon">🔮</div>
            <h2 className="calc-hub__section-title">Coming Soon</h2>
            <span className="calc-hub__section-count">{coming.length} planned</span>
          </div>
          <div className="calc-hub__grid">
            {coming.map((calc) => (
              <div key={calc.id} className="calc-hub__card calc-hub__card--disabled">
                <div className="calc-hub__card-icon calc-hub__card-icon--disabled">
                  <calc.Icon />
                </div>
                <div className="calc-hub__card-body">
                  <h3>{calc.label}</h3>
                  <p>{calc.desc}</p>
                  <div className="calc-hub__tags">
                    {calc.tags.map((tag) => (
                      <span key={tag} className="calc-hub__tag calc-hub__tag--muted">{tag}</span>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Disclaimer */}
      <div className="calc-hub__disclaimer">
        ⚠️ These calculators are for <strong>quantity estimating purposes only</strong> and do not constitute structural engineering design advice. Always consult a qualified structural engineer.
      </div>
    </div>
  );
};

export default Calculators;